import { api } from "./api";

const MAX_ENTRIES = 48;
const MAX_BYTES = 32 * 1024 * 1024;

type CacheEntry = {
  blob: Blob;
  size: number;
};

type PendingEntry = {
  controller: AbortController;
  promise: Promise<Blob>;
};

const entries = new Map<string, CacheEntry>();
const pending = new Map<string, PendingEntry>();
let totalBytes = 0;
let cacheVersion = 0;

export function attachmentThumbnailBlob(attachmentId: string): Promise<Blob> {
  const cached = entries.get(attachmentId);
  if (cached) {
    entries.delete(attachmentId);
    entries.set(attachmentId, cached);
    return Promise.resolve(cached.blob);
  }

  const inFlight = pending.get(attachmentId);
  if (inFlight) return inFlight.promise;

  const controller = new AbortController();
  const currentVersion = cacheVersion;
  const promise = api.attachmentBlob(attachmentId, "thumbnail", controller.signal)
    .then((blob) => {
      if (currentVersion !== cacheVersion) throw new DOMException("Cache was cleared", "AbortError");
      entries.set(attachmentId, { blob, size: blob.size });
      totalBytes += blob.size;
      trimCache(attachmentId);
      return blob;
    })
    .finally(() => {
      if (pending.get(attachmentId)?.promise === promise) pending.delete(attachmentId);
    });
  pending.set(attachmentId, { controller, promise });
  return promise;
}

export function clearAttachmentImageCache() {
  cacheVersion += 1;
  pending.forEach(({ controller }) => controller.abort());
  pending.clear();
  entries.clear();
  totalBytes = 0;
}

function trimCache(newestId: string) {
  while ((entries.size > MAX_ENTRIES || totalBytes > MAX_BYTES) && entries.size > 1) {
    const oldestId = entries.keys().next().value as string | undefined;
    if (!oldestId) return;
    if (oldestId === newestId) {
      const newest = entries.get(oldestId);
      entries.delete(oldestId);
      if (newest) entries.set(oldestId, newest);
      continue;
    }
    const oldest = entries.get(oldestId);
    entries.delete(oldestId);
    if (oldest) {
      totalBytes -= oldest.size;
    }
  }
}
